// The AI-model override panel (RB1's ChooseAIModelDialog body, re-homed as a
// DevToolsDialog tab per the DT1 dev order — one hotkey, one dialog). Gates,
// model catalog and store writes are v3 ChooseAIModelModal verbatim.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AppConfig } from '@/config/appConfig';
import { useCurrentUser, useEnabledFeatures } from '@/hooks/dataHelpers';
import { selectOriginalUserRole, useAuthStore } from '@/store';
import { selectAIModel, selectAIModelTemp, useOrgStore } from '@/store/orgStore';
import { modelOptions } from './modelOptions';

export const ChooseAIModelPanel = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const originalUserRole = useAuthStore(selectOriginalUserRole);
  const enabledFeatures = useEnabledFeatures();
  const modelSelectionEnabled = enabledFeatures.choose_ai_model;
  const notProductionMode = AppConfig.mode !== 'production';
  const currentUser = useCurrentUser();
  const setAIModel = useOrgStore((s) => s.setAIModel);
  const setAIModelTemp = useOrgStore((s) => s.setAIModelTemp);
  const aiModel = useOrgStore(selectAIModel);
  const aiModelTemp = useOrgStore(selectAIModelTemp);
  // v3 gates, verbatim.
  const isModelVisible = notProductionMode || currentUser?.role === 'qb_author';
  const isAiModelTempVisible =
    notProductionMode || (modelSelectionEnabled && originalUserRole === 'superadmin');

  // Panel remounts per dialog open / tab activation, so the store seeds here
  // (uncommitted picks discard on close — v3 semantics).
  const [activeAiModel, setActiveAiModel] = useState<string>(aiModel);
  const [activeAiModelTemp, setActiveAiModelTemp] = useState<number | null>(aiModelTemp);

  const handleSave = () => {
    setAIModel(activeAiModel);
    setAIModelTemp(activeAiModelTemp);
    onClose();
  };

  return (
    <div className="flex flex-col gap-4" data-testid="choose-ai-model-panel">
      <div className="grid gap-1">
        <h3 className="text-base font-semibold">
          {t('ChooseAIModelModal.choose_base_ai_model')}
          {originalUserRole === 'superadmin'
            ? ` (${t('CommonText.only_superadmin_support_can_see_this_feature')})`
            : ''}
        </h3>
        {!modelSelectionEnabled && (
          <div className="grid gap-1 text-sm text-muted-foreground">
            <span className="italic">
              {t('ChooseAIModelModal.defaults_gpt35turbo_for_chat_gpt4o_for_everything_else')}
            </span>
            <span className="italic">
              {t('ChooseAIModelModal.note_gpt4_will_default_to_4o_when_vision_needed')}
            </span>
          </div>
        )}
      </div>

      {/* One selection across all provider groups (v3 shared Radio value). */}
      <RadioGroup
        value={activeAiModel}
        onValueChange={setActiveAiModel}
        className="grid gap-4"
        data-testid="choose-ai-model-options"
      >
        {Object.keys(modelOptions).map((provider) => {
          const visibleModels = modelOptions[provider].filter(
            (model) => isModelVisible || (modelSelectionEnabled && model.schoolSelectable)
          );
          if (visibleModels.length === 0) return null;
          return (
            <div key={`ai-models-${provider}`} className="grid gap-2">
              <span className="text-sm font-semibold">{provider}</span>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                {visibleModels.map((model) => (
                  <Label
                    key={`ai-model-${model.value}`}
                    className="flex items-center gap-2 text-sm font-normal"
                  >
                    <RadioGroupItem
                      value={model.value}
                      disabled={model.disabled}
                      data-testid={`choose-ai-model-${model.value}`}
                    />
                    {model.name}
                  </Label>
                ))}
              </div>
            </div>
          );
        })}
      </RadioGroup>

      {isAiModelTempVisible && (
        <div className="grid gap-2 border-t border-border pt-4">
          <span className="text-sm font-semibold">{t('ChooseAIModelModal.other_options')}</span>
          <Input
            type="number"
            min={0.1}
            max={2}
            step={0.1}
            value={activeAiModelTemp ?? ''}
            onChange={(event) =>
              setActiveAiModelTemp(event.target.value === '' ? null : Number(event.target.value))
            }
            placeholder={t('ChooseAIModelModal.temp')}
            className="w-48"
            data-testid="choose-ai-model-temp"
          />
          <p className="text-xs text-muted-foreground">
            {t('ChooseAIModelModal.lower_is_more_conservative_higher_is_more_creative_01_for')}
          </p>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          data-testid="choose-ai-model-cancel"
        >
          {t('CommonText.cancel')}
        </Button>
        <Button type="button" onClick={handleSave} data-testid="choose-ai-model-save">
          {t('CommonText.save')}
        </Button>
      </div>
    </div>
  );
};
